import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  ChatBubbleOutline as CommentIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  DashboardOverview,
  OverviewPeriod,
  statsService,
} from '../services/statsService';

const INK = '#2D3748';
const MUTED = '#718096';
const GRID = '#E2E8F0';
const BAR = '#2E7D32';
const BAR_HOVER = '#1B5E20';
const TRACK = '#EDF2F7';

const number = (value: number) => value.toLocaleString('pt-BR');
const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const percent = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–';

/** "2026-10-11" -> "11/10" */
const shortDate = (key: string) => {
  const [, m, d] = key.split('-');
  return `${d}/${m}`;
};

const longDate = (key: string) =>
  new Date(`${key}T12:00:00`).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

/** "há 3 dias", "hoje", "nunca voltou" */
const sinceLabel = (iso: string | null) => {
  if (!iso) return 'Nunca voltou';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Hoje';
  if (days === 1) return 'Ontem';
  return `Há ${days} dias`;
};

// ─── Peças ─────────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: '100%' }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem' }}>{title}</Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

/** Variação contra o período anterior; subir é bom em todas as métricas daqui */
const Delta: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
  if (previous === 0 && current === 0) {
    return <Typography variant="body2" sx={{ color: MUTED }}>Sem movimento nos dois períodos</Typography>;
  }
  if (previous === 0) {
    return <Typography variant="body2" sx={{ color: MUTED }}>Nenhum no período anterior</Typography>;
  }
  const change = Math.round(((current - previous) / previous) * 100);
  const up = change >= 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {up ? (
        <ArrowUpIcon sx={{ fontSize: 16, color: '#2E7D32' }} />
      ) : (
        <ArrowDownIcon sx={{ fontSize: 16, color: '#C53030' }} />
      )}
      <Typography variant="body2" sx={{ color: up ? '#2E7D32' : '#C53030', fontWeight: 600 }}>
        {Math.abs(change)}%
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED }}>
        vs. período anterior ({number(previous)})
      </Typography>
    </Box>
  );
};

const StatTile: React.FC<{
  label: string;
  value: string;
  detail?: React.ReactNode;
}> = ({ label, value, detail }) => (
  <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Typography variant="body2" sx={{ color: MUTED, fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: '1.75rem', sm: '2rem' }, fontWeight: 700, color: INK, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {detail && <Box sx={{ mt: 1 }}>{detail}</Box>}
    </CardContent>
  </Card>
);

/** Colunas por dia, uma série, com dica ao passar o mouse e tabela opcional */
const DailyColumns: React.FC<{
  data: { date: string; value: number; note?: string }[];
  unit: string;
}> = ({ data, unit }) => {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  // Desenha na largura real, para o texto do eixo não encolher junto
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, Math.round(entry.contentRect.width))),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [asTable]);

  const height = 180;
  const pad = { top: 12, right: 8, bottom: 24, left: 32 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = Math.max(...data.map((d) => d.value), 1);
  const step = max <= 4 ? 1 : Math.ceil(max / 4);
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const slot = plotW / data.length;
  const bar = Math.min(18, Math.max(2, slot - 2));
  const y = (v: number) => pad.top + plotH - (v / top) * plotH;
  const labelEvery = Math.ceil(data.length / Math.max(3, Math.floor(plotW / 64)));
  const active = hover !== null ? data[hover] : null;

  return (
    <Box>
      {asTable ? (
        <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dia</TableCell>
                <TableCell align="right">{unit}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...data].reverse().map((d) => (
                <TableRow key={d.date}>
                  <TableCell>{longDate(d.date)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {number(d.value)}
                    {d.note ? ` · ${d.note}` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box ref={box} sx={{ position: 'relative' }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            role="img"
            aria-label={`${unit} por dia`}
            onMouseLeave={() => setHover(null)}
            style={{ display: 'block' }}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
                <text x={pad.left - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={MUTED} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {t}
                </text>
              </g>
            ))}
            {data.map((d, i) => {
              const cx = pad.left + slot * i + slot / 2;
              const h = (d.value / top) * plotH;
              const r = Math.min(4, bar / 2, h);
              const x0 = cx - bar / 2;
              const base = pad.top + plotH;
              return (
                <g key={d.date} onMouseEnter={() => setHover(i)}>
                  <rect x={pad.left + slot * i} y={pad.top} width={slot} height={plotH} fill="transparent" />
                  {d.value > 0 && (
                    <path
                      d={`M${x0},${base} V${base - h + r} Q${x0},${base - h} ${x0 + r},${base - h} H${x0 + bar - r} Q${x0 + bar},${base - h} ${x0 + bar},${base - h + r} V${base} Z`}
                      fill={hover === i ? BAR_HOVER : BAR}
                    />
                  )}
                  {(i === data.length - 1 ||
                    (i % labelEvery === 0 && data.length - 1 - i >= labelEvery)) && (
                    <text
                      x={i === data.length - 1 ? width - pad.right : cx}
                      y={height - 6}
                      textAnchor={i === data.length - 1 ? 'end' : 'middle'}
                      fontSize={11}
                      fill={MUTED}
                    >
                      {shortDate(d.date)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {active && hover !== null && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: `${((pad.left + slot * hover + slot / 2) / width) * 100}%`,
                transform: hover > data.length / 2 ? 'translateX(-105%)' : 'translateX(5%)',
                bgcolor: '#FFFFFF',
                border: `1px solid ${GRID}`,
                borderRadius: 1.5,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                px: 1.5,
                py: 1,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                {longDate(active.date)}
              </Typography>
              <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>
                {number(active.value)} {unit.toLowerCase()}
              </Typography>
              {active.note && (
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {active.note}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      <Link component="button" variant="body2" onClick={() => setAsTable(!asTable)} sx={{ mt: 1, color: MUTED }}>
        {asTable ? 'Ver gráfico' : 'Ver como tabela'}
      </Link>
    </Box>
  );
};

/** Barras horizontais com rótulo, valor e % sobre uma base */
const HorizontalBars: React.FC<{
  rows: { label: string; value: number; hint?: string }[];
  base: number;
}> = ({ rows, base }) => {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((r) => (
        <Box key={r.label} title={r.hint}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: INK }}>
              {r.label}
            </Typography>
            <Typography variant="body2" sx={{ color: INK, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {number(r.value)}
              <Box component="span" sx={{ color: MUTED, fontWeight: 400, ml: 0.75 }}>
                {percent(r.value, base)}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ height: 10, bgcolor: TRACK, borderRadius: 1 }}>
            <Box
              sx={{
                height: '100%',
                width: `${(r.value / max) * 100}%`,
                minWidth: r.value > 0 ? 4 : 0,
                bgcolor: BAR,
                borderRadius: 1,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ─── Página ────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState<OverviewPeriod>(30);
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (period: OverviewPeriod) => {
    setLoading(true);
    try {
      setData(await statsService.getOverview(period));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar os dados do painel.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        {error ? (
          <Alert severity="error" action={<Button onClick={() => load(days)}>Tentar de novo</Button>}>
            {error}
          </Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  const { users, subscriptions, engagement, funnel, lastActive } = data;
  const periodLabel = days === 7 ? '7 dias' : `${days} dias`;
  const accessRows = [
    { label: 'Hoje', value: lastActive.today },
    { label: 'Há 1 ou 2 dias', value: lastActive.days1to2 },
    { label: 'Há 3 a 6 dias', value: lastActive.days3to6 },
    { label: 'Há 1 a 2 semanas', value: lastActive.days7to13 },
    { label: 'Há 2 a 4 semanas', value: lastActive.days14to29 },
    { label: 'Há mais de 30 dias', value: lastActive.days30plus },
    { label: 'Nunca voltou depois do cadastro', value: lastActive.never },
  ];

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
      {/* Cabeçalho e período */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: INK }}>
            Visão geral
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED }}>
            {longDate(data.range.from)} a {longDate(data.range.to)} · horário de Brasília
            {users.internalExcluded > 0 &&
              ` · sem ${users.internalExcluded} ${users.internalExcluded === 1 ? 'conta interna' : 'contas internas'}`}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={days}
          exclusive
          size="small"
          onChange={(_, value) => value && setDays(value)}
          aria-label="Período"
        >
          <ToggleButton value={7}>7 dias</ToggleButton>
          <ToggleButton value={30}>30 dias</ToggleButton>
          <ToggleButton value={90}>90 dias</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data.pendingComments > 0 && (
        <Alert
          severity="info"
          icon={<CommentIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/comments')}>
              Moderar
            </Button>
          }
        >
          {data.pendingComments === 1
            ? '1 comentário aguardando moderação'
            : `${data.pendingComments} comentários aguardando moderação`}
        </Alert>
      )}

      {/* Números do período */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <StatTile
          label="Ativos nos últimos 7 dias"
          value={number(users.active7)}
          detail={
            <Typography variant="body2" sx={{ color: MUTED }}>
              {percent(users.active7, users.total)} de {number(users.total)} usuários · {number(users.active30)} no mês
            </Typography>
          }
        />
        <StatTile
          label={`Novos cadastros (${periodLabel})`}
          value={number(users.new)}
          detail={<Delta current={users.new} previous={users.newPrevious} />}
        />
        <StatTile
          label={`Devocionais concluídos (${periodLabel})`}
          value={number(engagement.completions)}
          detail={<Delta current={engagement.completions} previous={engagement.completionsPrevious} />}
        />
        <StatTile
          label="Assinantes pagantes"
          value={number(subscriptions.paying)}
          detail={
            <Typography variant="body2" sx={{ color: MUTED }}>
              {money(subscriptions.mrr)}/mês · {number(subscriptions.trial)} em teste grátis
              {subscriptions.courtesy > 0 && ` · ${subscriptions.courtesy} cortesia`}
            </Typography>
          }
        />
      </Box>

      {/* Por dia */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Section
          title="Devocionais concluídos por dia"
          subtitle={`${number(engagement.learners)} pessoas concluíram ao menos um no período`}
        >
          <DailyColumns
            unit="Devocionais"
            data={data.series.map((d) => ({
              date: d.date,
              value: d.completions,
              note: d.learners ? `${d.learners} ${d.learners === 1 ? 'pessoa' : 'pessoas'}` : undefined,
            }))}
          />
        </Section>
        <Section title="Cadastros por dia" subtitle={`${number(users.new)} no período`}>
          <DailyColumns unit="Cadastros" data={data.series.map((d) => ({ date: d.date, value: d.signups }))} />
        </Section>
      </Box>

      {/* Jornada e retorno */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Section
          title="Jornada dos usuários"
          subtitle="Desde o início · % sobre quem se cadastrou"
        >
          <HorizontalBars
            base={funnel.signedUp}
            rows={[
              { label: 'Cadastraram', value: funnel.signedUp },
              { label: 'Começaram uma trilha', value: funnel.startedTrail },
              { label: 'Concluíram o 1º devocional', value: funnel.firstDevotional },
              { label: 'Concluíram 7 ou mais', value: funnel.sevenDevotionals },
              { label: 'Abriram o app nos últimos 7 dias', value: funnel.activeLast7 },
            ]}
          />
        </Section>
        <Section
          title="Último acesso ao app"
          subtitle={`${number(users.withPush)} de ${number(users.total)} recebem notificação push`}
        >
          <HorizontalBars base={users.total} rows={accessRows} />
        </Section>
      </Box>

      {/* Trilhas e últimos cadastros */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        <Section
          title="Trilhas"
          action={
            <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/progress')}>
              Progresso
            </Button>
          }
        >
          {data.trails.length === 0 ? (
            <Typography variant="body2" sx={{ color: MUTED }}>
              Ninguém começou uma trilha ainda.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: MUTED }}>Trilha</TableCell>
                    <TableCell align="right" sx={{ color: MUTED }}>Em andamento</TableCell>
                    <TableCell align="right" sx={{ color: MUTED }}>Ativos na semana</TableCell>
                    <TableCell align="right" sx={{ color: MUTED }}>Concluíram</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.trails.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell sx={{ color: INK, fontWeight: 600 }}>{t.title}</TableCell>
                      {[t.inProgress, t.active7, t.completed].map((v, i) => (
                        <TableCell key={i} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {number(v)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Section>
        <Section
          title="Últimos cadastros"
          action={
            <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/users')}>
              Usuários
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {data.recentUsers.map((u, i) => (
              <Box
                key={u.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.25,
                  borderTop: i === 0 ? 'none' : `1px solid ${GRID}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }} noWrap>
                    {u.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>
                    {u.email}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', mb: 0.25 }}>
                    <Chip size="small" variant="outlined" label={u.via === 'google' ? 'Google' : 'E-mail'} />
                    {u.isPremium && <Chip size="small" color="warning" label="Premium" />}
                  </Box>
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    {new Date(u.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} · {sinceLabel(u.lastActiveAt)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Section>
      </Box>
    </Box>
  );
};

export default Dashboard;
